import React, { useState, useRef, useEffect } from "react";
import Select, { components } from "react-select";
import { FaChevronDown } from "react-icons/fa";
import CloseIcon from "../images/white-close-icon.png";
import AppConstants from "../AppConstants";
import axios from "axios";

const radiusOptions = [
  { value: "20", label: "Within 20 miles" },
  { value: "50", label: "Within 50 miles" },
  { value: "100", label: "Within 100 miles" },
  { value: "250", label: "Within 250 miles" }
];

const Option = (props) => {
  const { isSelected, label } = props;
  return (
    <components.Option {...props}>
      <div className="checkbox-option">
        <input type="checkbox" checked={isSelected} readOnly />
        <label>{label}</label>
      </div>
    </components.Option>
  );
};

const LocationSelector = ({ value = {}, onChange }) => {
  const wrapperRef = useRef(null);
  const [tab, setTab] = useState("city");

  // State list fetched from server
  const [stateOptions, setStateOptions] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [errorStates, setErrorStates] = useState(null);

  // City suggestions from API
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);

  const [isOpen, setIsOpen] = useState(false);

  // Controlled values FROM PARENT
  const city = value.city || "";
  const radius =
    radiusOptions.find((r) => r.value === (value.radius || "20")) ||
    radiusOptions[0];
  const selectedStateCodes = value.states || [];

  const selectedStates = stateOptions.filter((opt) =>
    selectedStateCodes.includes(opt.value)
  );

  // Fetch states on mount
  useEffect(() => {
    setLoadingStates(true);
    axios
      .get(AppConstants.Jobs_API + "/jobs/getAllStates")
      .then((res) => {
        const mapped = res.data.data.map((item) => ({
          label: item.label, // "Alaska"
          value: item.code // "AK"
        }));

        const extraOptions = [
          { label: "All", value: "All" },
          { label: "Compact", value: "Compact" }
        ];
        setStateOptions([...extraOptions, ...mapped]);
      })
      .catch((error) => {
        console.error("Error fetching states:", error);
        setErrorStates(error);
      })
      .finally(() => setLoadingStates(false));
  }, []);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        event.button === 2 ||
        (wrapperRef.current && wrapperRef.current.contains(event.target)) ||
        event.target.closest(".react-select__menu")
      ) {
        return;
      }
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch city suggestions as user types
  useEffect(() => {
    if (city.length < 3) {
      setCitySuggestions([]);
      return;
    }
    setLoadingCities(true);

    axios
      .post(
        `${AppConstants.Jobs_API}/jobs/getAllCitiesWithAutoLoadAndJobCount`,
        { searchtext: city }
      )
      .then((res) => {
        if (res.data && Array.isArray(res.data.data)) {
          setCitySuggestions(res.data.data);
        } else {
          setCitySuggestions([]);
        }
      })
      .catch((err) => {
        console.error("Error fetching cities:", err);
        setCitySuggestions([]);
      })
      .finally(() => setLoadingCities(false));
  }, [city]);

  const renderTriggerText = () => {
    if (tab === "city" && city) return `${city} - ${radius.value}m`;
    if (tab === "state" && selectedStates.length)
      return `${selectedStates.length} Selected`;
    return "All";
  };

  return (
    <div className="location-selector" ref={wrapperRef}>
      <div className="dropdown-trigger" onClick={() => setIsOpen(!isOpen)}>
        <span className="location-label">{renderTriggerText()}</span>
        <FaChevronDown className="mls-chevro" />
      </div>

      {selectedStates.length > 0 && (
        <div className="selected-tags">
          {selectedStates.map((state) => (
            <span key={state.value} className="tag">
              {state.label}
              <img
                className="mls-close-icon"
                onClick={() => {
                  const newCodes = selectedStateCodes.filter(
                    (code) => code !== state.value
                  );
                  onChange({ locationStates: newCodes });
                }}
                src={CloseIcon}
                alt="close"
              />
            </span>
          ))}
        </div>
      )}

      {isOpen && (
        <div className="dropdown-panel">
          <div className="tabs">
            <button
              className={tab === "city" ? "active" : ""}
              onClick={() => setTab("city")}
            >
              City
            </button>
            <button
              className={tab === "state" ? "active" : ""}
              onClick={() => setTab("state")}
            >
              State
            </button>
          </div>

          {tab === "city" && (
            <div className="city-tab">
              <input
                type="text"
                placeholder="City"
                value={city}
                onChange={(e) => onChange({ locationCity: e.target.value })}
                className="city-input"
              />

              {loadingCities && (
                <div className="city-suggestion">Loading...</div>
              )}

              {!loadingCities &&
                citySuggestions.map((suggestion) => (
                  <div
                    key={suggestion.value}
                    className="city-suggestion"
                    onClick={() => onChange({ locationCity: suggestion.label })}
                  >
                    {suggestion.label}
                  </div>
                ))}

              <Select
                className="radius-select"
                classNamePrefix="react-select"
                options={radiusOptions}
                value={radius}
                onChange={(selected) =>
                  onChange({ locationRadius: selected.value })
                }
                placeholder="Select radius"
              />
            </div>
          )}

          {tab === "state" && (
            <div className="state-tab">
              {loadingStates && <p>Loading states...</p>}
              {errorStates && <p className="error">Error loading states</p>}
              {!loadingStates && !errorStates && (
                <Select
                  options={stateOptions}
                  isMulti
                  placeholder="Search..."
                  value={selectedStates}
                  onChange={(newSelected, actionMeta) => {
                    if (!newSelected) {
                      onChange({ locationStates: [] });
                      return;
                    }

                    const lastClicked = actionMeta.option?.value;

                    if (lastClicked === "All") {
                      // User clicked All -> All alone
                      onChange({ locationStates: ["All"] });
                      return;
                    }

                    if (lastClicked === "Compact") {
                      // User clicked Compact -> Compact alone
                      onChange({ locationStates: ["Compact"] });
                      return;
                    }

                    // Remove All or Compact if they were there
                    const cleaned = newSelected
                      .map((s) => s.value)
                      .filter((v) => v !== "All" && v !== "Compact");

                    onChange({ locationStates: cleaned });
                  }}
                  className="state-select"
                  classNamePrefix="react-select"
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  components={{ Option, MultiValue: () => null }}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LocationSelector;
